import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import WarehousesListScreen from './screens/WarehousesListScreen';
import WarehouseScreen from './screens/WarehouseUpdateScreen';
import Header from './components/Header';
import Footer from './components/Footer';
import CreateWarehouseScreen from './screens/WarehouseCreateScreen'
import VendorsListScreen from './screens/VendorsListScreen'
import VendorScreen from './screens/VendorUpdateScreen'
import CreateVendorScreen from './screens/VendorCreateScreen'
import BrandsListScreen from './screens/BrandsListScreen'
import BrandUpdateScreen from './screens/BrandUpdateScreen'
import BrandCreateScreen from './screens/BrandCreateScreen'
import LoginScreen from './screens/LoginScreen';
import ChangelogModal from './components/ChangelogModal';
import { versionConfig } from './version';

import DashboardScreen from './screens/DashboardScreen';
import UsersListScreen from './screens/UsersListScreen';
import UserScreen from './screens/UserUpdateScreen';
import CreateUserScreen from './screens/UserCreateScreen';
import UserPasswordChangeScreen from './screens/UserPasswordChangeScreen';
import UserProfileScreen from './screens/UserProfileScreen';
import LocationsListScreen from './screens/LocationsListScreen';
import LocationCreateScreen from './screens/LocationCreateScreen';
import LocationUpdateScreen from './screens/LocationUpdateScreen';
import StockItemCategoriesListScreen from './screens/StockItemCategoriesListScreen';
import StockItemCategoryUpdateScreen from './screens/StockItemCategoryUpdateScreen';
import StockItemCategoryCreateScreen from './screens/StockItemCategoryCreateScreen';
import StockItemsListScreen from './screens/StockItemsListScreen';
import StockItemDetailsScreen from './screens/StockItemDetailsScreen';
import InventoryReceiveScreen from './screens/InventoryReceiveScreen';
import BatchReceiveScreen from './screens/BatchReceiveScreen';
import BulkReceiveScreen from './screens/BulkReceiveScreen';
import BarcodePrintScreen from './screens/BarcodePrintScreen';
import InventoryListScreen from './screens/InventoryListScreen';
import BarcodeScanScreen from './screens/BarcodeScanScreen';
import InventoryHistoryListScreen from './screens/InventoryHistoryListScreen';
import InventoryAmendScreen from './screens/InventoryAmendScreen';
import InventoryMoveScreen from './screens/InventoryMoveScreen';
import InventoryDisposeScreen from './screens/InventoryDisposeScreen';
import InventoryReturnScreen from './screens/InventoryReturnScreen';
import InventoryTransferScreen from './screens/InventoryTransferScreen';
import InventoryTransfersListScreen from './screens/InventoryTransfersListScreen';
import TransferReceiveScreen from './screens/TransferReceiveScreen';
import IssueScreen from './screens/IssueScreen';
import AIAssistantScreen from './screens/AIAssistantScreen';
import PurchaseOrdersListScreen from './screens/PurchaseOrdersListScreen'
import PurchaseOrderDetailsScreen from './screens/PurchaseOrderDetailsScreen'
import SystemSettingsScreen from './screens/SystemSettingsScreen'
import StockForecastScreen from './screens/StockForecastScreen'
import AboutScreen from './screens/AboutScreen'
import IssuedInventoryReportScreen from './screens/IssuedInventoryReportScreen'
import IssuedInventoryListScreen from './screens/IssuedInventoryListScreen'
import IssueDestinationsListScreen from './screens/IssueDestinationsListScreen'
import IssueDestinationCreateScreen from './screens/IssueDestinationCreateScreen'
import IssueDestinationDetailsScreen from './screens/IssueDestinationDetailsScreen'
import IssueDestinationSubcategoryCreateScreen from './screens/IssueDestinationSubcategoryCreateScreen'
import IssueDestinationSubcategoryDetailsScreen from './screens/IssueDestinationSubcategoryDetailsScreen'
import UserPermissionManagementScreen from './screens/UserPermissionManagementScreen'
import ItemFamilyListScreen from './screens/ItemFamilyListScreen'
import ItemFamilyUpdateScreen from './screens/ItemFamilyUpdateScreen'
import RecipeListScreen from './screens/RecipeListScreen'
import RecipeUpdateScreen from './screens/RecipeUpdateScreen'
import RecipeExecuteScreen from './screens/RecipeExecuteScreen'
import ProductionListScreen from './screens/ProductionListScreen'
import ProductionDetailScreen from './screens/ProductionDetailScreen'
import ProductionFinalizeScreen from './screens/ProductionFinalizeScreen'
import { PermissionProvider } from './contexts/PermissionContext'
import { WarehouseProvider } from './contexts/WarehouseContext'
import WarehouseSelectorModal from './components/WarehouseSelectorModal'


function ProtectedLayout() {
  const userToken = localStorage.getItem('userToken');
  const [showChangelog, setShowChangelog] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);

  useEffect(() => {
    if (!userToken) {
      return;
    }
    // Skip version check in dev mode if disabled
    if (process.env.REACT_APP_DISABLE_VERSION_CHECK === 'true') {
      return;
    }

    const checkVersion = () => {
      const lastSeenVersion = localStorage.getItem('last_seen_version') || '0.0.0';
      const currentVersion = versionConfig.version;

      // Check if there's a new version
      if (lastSeenVersion !== currentVersion) {
        const { showNotification, forceReset, softReset } = versionConfig;

        // Perform reset if required (preserve auth, clear everything else)
        if (forceReset) {
          const userToken = localStorage.getItem('userToken');
          const username = localStorage.getItem('username');
          const userFirstName = localStorage.getItem('user_first_name');

          // Clear localStorage
          localStorage.clear();

          // Restore auth and mark version as seen to prevent infinite loop
          if (userToken) localStorage.setItem('userToken', userToken);
          if (username) localStorage.setItem('username', username);
          if (userFirstName) localStorage.setItem('user_first_name', userFirstName);
          localStorage.setItem('last_seen_version', currentVersion);
        } else if (softReset) {
          // Clear only table/column settings, preserve everything else
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (
              key.startsWith('table_widths_') ||
              key.startsWith('table_column_order_') ||
              key.startsWith('table_total_width_') ||
              key.startsWith('table_state_') ||
              key.endsWith('ColumnWidths') ||
              key.endsWith('ColumnVisibility')
            ) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          localStorage.setItem('last_seen_version', currentVersion);
        }

        // Show notification if required
        if (showNotification) {
          setVersionInfo(versionConfig);
          setShowChangelog(true);
        } else {
          // Just mark as seen without showing modal
          localStorage.setItem('last_seen_version', currentVersion);
        }
      }
    };

    checkVersion();
  }, [userToken]);

  const handleCloseChangelog = () => {
    setShowChangelog(false);

    // Reload if reset was performed to apply cleared settings
    if (versionConfig.forceReset || versionConfig.softReset) {
      window.location.reload();
    }
  };

  if (!userToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <PermissionProvider>
      <WarehouseProvider>
        <div className="app-container">
          <Header/>
        <main>
          <Routes>
          <Route path="/dashboard" element={<DashboardScreen />} />
          <Route path="/warehouses" element={<WarehousesListScreen />} />
          <Route path="/warehouse/:id" element={<WarehouseScreen />} />
          <Route path="/warehouse/create" element={<CreateWarehouseScreen />} />
          <Route path="/vendors" element={<VendorsListScreen />} />
          <Route path="/vendor/:id" element={<VendorScreen />} />
          <Route path="/vendor/create" element={<CreateVendorScreen />} />
          <Route path="/brands" element={<BrandsListScreen />} />
          <Route path="/brand/:id" element={<BrandUpdateScreen />} />
          <Route path="/brand/create" element={<BrandCreateScreen />} />
          <Route path="/users" element={<UsersListScreen />} />
          <Route path="/user/:id" element={<UserScreen />} />
          <Route path="/user/create" element={<CreateUserScreen />} />
          <Route path="/user/:id/change-password" element={<UserPasswordChangeScreen />} />
          <Route path="/user/:userId/permissions" element={<UserPermissionManagementScreen />} />
          <Route path="/profile" element={<UserProfileScreen />} />
          <Route path="/warehouse/:warehouseId/locations" element={<LocationsListScreen />} />
          <Route path="/location/create" element={<LocationCreateScreen />} />
          <Route path="/location/:id" element={<LocationUpdateScreen />} />
          <Route path="/stock-item-categories" element={<StockItemCategoriesListScreen />} />
          <Route path="/stock-item-category/:id" element={<StockItemCategoryUpdateScreen />} />
          <Route path="/stock-item-category/create" element={<StockItemCategoryCreateScreen />} />
          <Route path="/stock-items" element={<StockItemsListScreen />} />
          <Route path="/stock-item/:id" element={<StockItemDetailsScreen />} />
          <Route path="/stock-item/create" element={<StockItemDetailsScreen />} />
          <Route path="/inventory-receive/:stockItemId" element={<InventoryReceiveScreen />} />
          <Route path="/inventory-receive-batch" element={<BatchReceiveScreen />} />
          <Route path="/bulk-receive" element={<BulkReceiveScreen />} />
          <Route path="/print-barcode" element={<BarcodePrintScreen />} />
          <Route path="/inventory" element={<InventoryListScreen />} />
          <Route path="/inventory-transfers" element={<InventoryTransfersListScreen />} />
          <Route path="/scan-barcode" element={<BarcodeScanScreen />} />
          <Route path="/inventory-history" element={<InventoryHistoryListScreen />} />
          <Route path="/inventory/:inventoryItemId/amend" element={<InventoryAmendScreen />} />
          <Route path="/inventory/:inventoryItemId/move" element={<InventoryMoveScreen />} />
          <Route path="/inventory/:inventoryItemId/dispose" element={<InventoryDisposeScreen />} />
          <Route path="/inventory/:inventoryItemId/return" element={<InventoryReturnScreen />} />
          <Route path="/inventory/:inventoryItemId/transfer" element={<InventoryTransferScreen />} />
          <Route path="/transfers/:transferId/receive" element={<TransferReceiveScreen />} />
          <Route path="/inventory/:inventoryItemId/issue" element={<IssueScreen />} />
          <Route path="/purchase-orders" element={<PurchaseOrdersListScreen />} />
          <Route path="/purchase-order/:id" element={<PurchaseOrderDetailsScreen />} />
          <Route path="/purchase-order/create" element={<PurchaseOrderDetailsScreen />} />
          <Route path="/ai-assistant" element={<AIAssistantScreen />} />
          <Route path="/system-settings" element={<SystemSettingsScreen />} />
          <Route path="/stock-forecast" element={<StockForecastScreen />} />
          <Route path="/about" element={<AboutScreen />} />
          <Route path="/issued-inventory" element={<IssuedInventoryListScreen />} />
          <Route path="/reports/issued-inventory" element={<IssuedInventoryReportScreen />} />
          <Route path="/issue-destinations" element={<IssueDestinationsListScreen />} />
          <Route path="/issue-destinations/create" element={<IssueDestinationCreateScreen />} />
          <Route path="/issue-destinations/:id" element={<IssueDestinationDetailsScreen />} />
          <Route path="/issue-destinations/subcategory/create" element={<IssueDestinationSubcategoryCreateScreen />} />
          <Route path="/issue-destinations/subcategory/:id" element={<IssueDestinationSubcategoryDetailsScreen />} />
          <Route path="/item-families" element={<ItemFamilyListScreen />} />
          <Route path="/item-family/create" element={<ItemFamilyUpdateScreen />} />
          <Route path="/item-family/:id" element={<ItemFamilyUpdateScreen />} />
          <Route path="/recipes" element={<RecipeListScreen />} />
          <Route path="/recipe/create" element={<RecipeUpdateScreen />} />
          <Route path="/recipe/:id" element={<RecipeUpdateScreen />} />
          <Route path="/recipe/:id/execute" element={<RecipeExecuteScreen />} />
          <Route path="/productions" element={<ProductionListScreen />} />
          <Route path="/production/:id" element={<ProductionDetailScreen />} />
          <Route path="/production/:id/finalize" element={<ProductionFinalizeScreen />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </main>
        <Footer/>
        <ChangelogModal
          show={showChangelog}
          onClose={handleCloseChangelog}
          versionInfo={versionInfo}
        />
        <WarehouseSelectorModal suppress={showChangelog} />
        </div>
      </WarehouseProvider>
    </PermissionProvider>
  );
}

function App() {
  useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/*" element={<ProtectedLayout />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

